import * as _ from "lodash";

import { IHierarchyNode, IHierarchyLevel } from "../PA.Core/PA.Core.IF.Hierarchy";
import {
    ICell,
    ICellMetricRenderOptions,
    ICellMetricUOM,
    ICubePerspective,
    ICubePerspectiveOptions,
    ICubeSource,
} from "../PA.HyperRectangle.Core/PA.HyperRectangle.IF";

export interface ICubeSourcePerspectiveData {
    Name: string;
    Dimensions: string[];
    Metrics: string[];

    CellData: ICubeSourcePerspectiveDataCell[];

    DimensionsData: {
        [dimensionType: string]: {
            HierarchyId: string;
            HierarchyIdUI: string;
            HierarchyName: string;
            Levels: { [levelId: string]: IHierarchyLevel };
            Nodes: { [nodeId: string]: IHierarchyNode };
            bHasSortOrder: boolean;
        };
    };
    MetricData: { [metricId: string]: ICubeSourcePerspectiveDataMetric };
}

export interface ICubeSourcePerspectiveDataCell {
    CellId: string;
    DimensionValues: { [dimensionType: string]: string };
    MetricValues: { [metricId: string]: number };
}

export interface ICubeSourcePerspectiveDataMetric {
    MetricId: string;
    Name: string;
    GroupName: string;
    bIsReadOnly: boolean;
    RenderOptions: ICellMetricRenderOptions;
    UOM: ICellMetricUOM;
}

export class CubeSourceDataService {
    static async createPerspectiveData(perspective: ICubePerspective): Promise<ICubeSourcePerspectiveData> {
        if (_.isEmpty(perspective)) return undefined;

        const cs = perspective.CubeSource;

        const rv: ICubeSourcePerspectiveData = {
            Name: perspective?.CubePerspectiveName || "",
            Dimensions: perspective.getDimensions(),
            Metrics: _.map(perspective.getCellMetrics(), (a) => a.MetricId),
            CellData: [],
            DimensionsData: {},
            MetricData: {},
        };

        // Add Dimension Meta Data
        const dimTypes = rv.Dimensions;
        _.forEach(dimTypes, (sDimType) => {
            const hier = cs.getDimensionHierarchy(<any>sDimType);

            rv.DimensionsData[sDimType] = {
                HierarchyId: hier.HierarchyId,
                HierarchyIdUI: hier.HierarchyIdUI,
                HierarchyName: hier.HierarchyName,
                bHasSortOrder: hier.bHasSortOrder,
                Levels: _.keyBy(hier.getLevels(), (lvl: IHierarchyLevel) => lvl.LevelId),
                Nodes: {},
            };
        });

        // Add Metric Meta Data
        _.forEach(rv.Metrics, (metricId) => {
            const mtrc = perspective.getCellMetric(metricId);
            rv.MetricData[metricId] = {
                MetricId: metricId,
                Name: mtrc.MetricName,
                GroupName: mtrc.GroupName,
                bIsReadOnly: mtrc.bIsReadOnly,
                RenderOptions: mtrc.RenderOptions,
                UOM: perspective.getCellMetricUOM(metricId),
            };
        });

        // Add Cell Data
        _.forEach(perspective.Cells, (cell: ICell) => {
            const cd: ICubeSourcePerspectiveDataCell = {
                CellId: cell.CellId,
                DimensionValues: {},
                MetricValues: {},
            };
            _.forEach(rv.Dimensions, (sDimType: string) => {
                const nodeId = cell.getKey(sDimType);
                const hier = cs.getDimensionHierarchy(<any>sDimType);
                const node = hier.getNode(nodeId);
                cd.DimensionValues[sDimType] = nodeId;
                const d = rv.DimensionsData[sDimType];
                if (!(nodeId in d.Nodes)) d.Nodes[nodeId] = node;
            });
            _.forEach(rv.Metrics, (metricId) => {
                const metricValue = cell.getCellMetricValue(metricId);
                cd.MetricValues[metricId] = <number>metricValue;
            });

            rv.CellData.push(cd);
        });

        return rv;
    }

    static async createPerspectiveDataUsingOptions(
        cubeSource: ICubeSource,
        perspectiveOptions: ICubePerspectiveOptions,
    ): Promise<ICubeSourcePerspectiveData> {
        if (_.isEmpty(cubeSource)) return undefined;

        if (_.isEmpty(perspectiveOptions)) return undefined;

        const p = cubeSource.createPerspective("Export", perspectiveOptions);
        await p.OnCubePerspectiveLoadComplete;
        return await CubeSourceDataService.createPerspectiveData(p);
    }
}
